import type { CopyPriceRequest, ProductDto } from '@/api';
import { CopyRule, FilterMode, LaunchStatusHy, PriceStatus } from '@/api';
import CombinedCombobox from '@/components/combobox/combined-combobox';
import Combobox, {
  type DefaultOptionType,
} from '@/components/combobox/combobox';
import ComboboxForPasting from '@/components/combobox/combobox-for-pasting';
import {
  useFilteredProducts,
  usePriceUnitsPasting,
  useProductsPasting,
} from '@/components/combobox/combobox-queries';
import GroupsCombobox from '@/components/combobox/groups-combobox';
import PriceUnitsCombobox from '@/components/combobox/price-units-combobox';
import ThemesCombobox from '@/components/combobox/themes-combobox';
import LeftSidePanel from '@/components/left-side-panel';
import { buildThemeGroupFilters } from '@/pages/price-planning/copy-price-page/copy-price-helpers';
import { toggleInArray } from '@/utils/array';
import {
  formatDateStringIso,
  getToday,
  parseDateString,
} from '@/utils/date-utils';
import {
  Button,
  Checkbox,
  ContentSwitcher,
  DatePicker,
  HelpText,
  Label,
  RadioButton,
} from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import type { MultiValue } from 'chakra-react-select';
import { useState } from 'react';
import { useEligibleTargets } from '../copy-price-queries';
import type { CopySettingsSummary } from './copy-settings-summary-panel';

const PRODUCT_STATUS_OPTIONS: { label: string; value: LaunchStatusHy }[] = [
  { label: '1HY', value: LaunchStatusHy.FirstHalf },
  { label: '2HY', value: LaunchStatusHy.SecondHalf },
  { label: 'C', value: LaunchStatusHy.CarryOver },
  { label: 'O', value: LaunchStatusHy.Outgoing },
];

const COPY_RULE_OPTIONS: { label: string; value: CopyRule }[] = [
  { label: 'RRP only', value: CopyRule.CopyRrpOnly },
  { label: 'LP only', value: CopyRule.CopyLpOnly },
  { label: 'LP and RRP', value: CopyRule.CopyRrpAndLp },
];

type CopySettingsPanelProps = {
  isLoading: boolean;
  onSubmit: (request: CopyPriceRequest, summary: CopySettingsSummary) => void;
};

export const CopySettingsPanel = ({
  isLoading,
  onSubmit,
}: CopySettingsPanelProps) => {
  const [validFromDate, setValidFromDate] = useState<Date | null>(null);

  const [copyFromSelected, setCopyFromSelected] = useState<DefaultOptionType[]>(
    [],
  );
  const [copyFromStatuses, setCopyFromStatuses] = useState<PriceStatus[]>([
    PriceStatus.Approved,
    PriceStatus.Preliminary,
  ]);

  const [copyToSearch, setCopyToSearch] = useState('');
  const [copyToMode, setCopyToMode] = useState<string>('paste');
  const [copyToPasteValue, setCopyToPasteValue] = useState<number[] | null>(
    null,
  );
  const [copyToSelected, setCopyToSelected] = useState<DefaultOptionType[]>([]);
  const [copyToStatuses, setCopyToStatuses] = useState<PriceStatus[]>([
    PriceStatus.Approved,
    PriceStatus.Preliminary,
  ]);

  const [productsSelected, setProductsSelected] = useState<DefaultOptionType[]>(
    [],
  );

  const [themesSelected, setThemesSelected] = useState<DefaultOptionType[]>([]);

  const [groupsSelected, setGroupsSelected] = useState<DefaultOptionType[]>([]);

  const [hasError, setHasError] = useState(false);

  const [productStatuses, setProductStatuses] = useState<LaunchStatusHy[]>([
    LaunchStatusHy.FirstHalf,
    LaunchStatusHy.SecondHalf,
    LaunchStatusHy.CarryOver,
    LaunchStatusHy.Outgoing,
  ]);

  const [copyRule, setCopyRule] = useState<CopyRule>(CopyRule.CopyRrpOnly);

  const copyFromFirst = copyFromSelected[0];
  const copyFromPriceUnitId =
    copyFromFirst !== undefined ? Number(copyFromFirst.id) : null;

  const { data: eligibleTargetsData, isFetching: isFetchingTargets } =
    useEligibleTargets(copyFromPriceUnitId, copyToSearch);

  const { data: copyToPasteData, isFetching: isFetchingCopyToPaste } =
    usePriceUnitsPasting(copyToPasteValue ?? []);

  const { validItems: copyToValidItems, noResultKeys: copyToNoResultKeys } =
    Object.entries(copyToPasteData ?? {}).reduce<{
      validItems: DefaultOptionType[];
      noResultKeys: string[];
    }>(
      (acc, [key, value]) => {
        if (value !== null) {
          acc.validItems.push({
            id: value.id.toString(),
            label: `${value.id} - ${value.name ?? 'N/A'}`,
            hasError: false,
          });
        } else {
          acc.noResultKeys.push(key);
        }
        return acc;
      },
      { validItems: [], noResultKeys: [] },
    );

  const copyToHasError = copyToNoResultKeys.length > 0;

  const isFormValid =
    validFromDate !== null &&
    copyFromSelected.length > 0 &&
    copyToSelected.length > 0 &&
    copyFromStatuses.length > 0 &&
    copyToStatuses.length > 0 &&
    productStatuses.length > 0 &&
    !hasError &&
    !copyToHasError;

  const handleSubmit = () => {
    const request: CopyPriceRequest = {
      validFromDate: validFromDate as Date,
      copyFromPriceUnitId:
        copyFromFirst !== undefined ? Number(copyFromFirst.id) : 0,
      copyToPriceUnitIds: copyToSelected.map((i) => Number(i.id)),
      copyFromPriceStatuses: copyFromStatuses,
      copyToPriceStatuses: copyToStatuses,
      ...buildThemeGroupFilters(themesSelected, groupsSelected),
      productIds:
        productsSelected.length > 0
          ? productsSelected.map((p) => Number(p.id))
          : undefined,
      productFilterMode:
        productsSelected.length > 0 ? FilterMode.Include : undefined,
      productStatuses: productStatuses.length > 0 ? productStatuses : undefined,
      copyRule,
    };

    const summary: CopySettingsSummary = {
      validFromDate: validFromDate as Date,
      copyFromLabel:
        copyFromFirst?.label.split(' - ').slice(1).join(' - ') ?? '',
      copyFromStatuses,
      copyToLabels: copyToSelected.map((i) =>
        i.label.split(' - ').slice(1).join(' - '),
      ),
      copyToStatuses,
      productsLabels: productsSelected.map((i) => i.label),
      themesLabels: themesSelected.map((i) => i.label),
      groupsLabels: groupsSelected.map((i) => i.label),
      productStatuses,
      copyRule,
    };

    onSubmit(request, summary);
  };

  return (
    <LeftSidePanel title="Copy settings">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        <div>
          <Label>Valid from</Label>
          <DatePicker
            mode="single"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            minDate={formatDateStringIso(getToday())}
            value={validFromDate ? formatDateStringIso(validFromDate) : null}
            onChange={(_, value) => {
              setValidFromDate(value ? parseDateString(value) : null);
            }}
          />
        </div>
        <Flex flexDir="column" gap="4px">
          <PriceUnitsCombobox
            title="Copy from"
            isSingleSelect
            selectedItems={copyFromSelected}
            onSelect={(selected: MultiValue<DefaultOptionType>) => {
              const items = [...selected] as DefaultOptionType[];
              if (items[0]?.id !== copyFromFirst?.id) {
                setCopyToSelected([]);
              }
              setCopyFromSelected(items);
            }}
          />
          <Flex>
            <Checkbox
              label="Approved"
              checked={copyFromStatuses.includes(PriceStatus.Approved)}
              onChange={() =>
                setCopyFromStatuses((prev) =>
                  toggleInArray(prev, PriceStatus.Approved),
                )
              }
            />
            <Checkbox
              label="Preliminary"
              checked={copyFromStatuses.includes(PriceStatus.Preliminary)}
              onChange={() =>
                setCopyFromStatuses((prev) =>
                  toggleInArray(prev, PriceStatus.Preliminary),
                )
              }
            />
          </Flex>
        </Flex>

        <Flex flexDir="column" gap="4px">
          <Flex justifyContent="space-between" alignItems="flex-end" mb={1}>
            <Label>Copy to</Label>
            <ContentSwitcher
              aria-label="Choose mode"
              width="hug"
              onSwitch={(value) => setCopyToMode(value)}
              size="small"
              disabled={copyToHasError || copyFromPriceUnitId === null}
            >
              <ContentSwitcher.Segment
                aria-controls="paste"
                text="Paste"
                value="paste"
              />
              <ContentSwitcher.Segment
                aria-controls="search"
                text="Search"
                value="search"
              />
            </ContentSwitcher>
          </Flex>
          {copyToMode === 'paste' ? (
            <ComboboxForPasting
              validItems={copyToValidItems}
              noResultKeys={copyToNoResultKeys}
              selectedItems={copyToSelected}
              hasError={copyToHasError}
              finalValue={copyToPasteValue}
              isFetching={isFetchingCopyToPaste}
              setFinalValue={setCopyToPasteValue}
              onSelect={(items) =>
                setCopyToSelected([...items] as DefaultOptionType[])
              }
              setHasError={() => {}}
              isDisabled={copyFromPriceUnitId === null}
            />
          ) : (
            <Combobox
              options={
                eligibleTargetsData?.map((item) => ({
                  id: item.id.toString(),
                  label: `${item.id} - ${item.name ?? 'N/A'}`,
                })) ?? []
              }
              selectedItems={copyToSelected}
              isLoading={isFetchingTargets}
              inputValue={copyToSearch}
              placeholder="Type planning price list id or description"
              isDisabled={copyFromPriceUnitId === null}
              onSelect={(items: MultiValue<DefaultOptionType>) =>
                setCopyToSelected([...items] as DefaultOptionType[])
              }
              onInputChange={setCopyToSearch}
            />
          )}
          <HelpText variant="neutral">
            Only price lists in the same currency.
          </HelpText>
          <Flex>
            <Checkbox
              label="Approved"
              checked={copyToStatuses.includes(PriceStatus.Approved)}
              onChange={() =>
                setCopyToStatuses((prev) =>
                  toggleInArray(prev, PriceStatus.Approved),
                )
              }
            />
            <Checkbox
              label="Preliminary"
              checked={copyToStatuses.includes(PriceStatus.Preliminary)}
              onChange={() =>
                setCopyToStatuses((prev) =>
                  toggleInArray(prev, PriceStatus.Preliminary),
                )
              }
            />
          </Flex>
        </Flex>

        <Flex flexDir="column" gap="12px">
          <CombinedCombobox<ProductDto>
            label="Products"
            useFilteredItems={useFilteredProducts}
            useItemsPasting={useProductsPasting}
            selectedItems={productsSelected}
            onSelect={(selected) =>
              setProductsSelected([...selected] as DefaultOptionType[])
            }
            setHasError={setHasError}
          />
          <ThemesCombobox
            selectedItems={themesSelected}
            onSelect={(selected) =>
              setThemesSelected([...selected] as DefaultOptionType[])
            }
          />
          <GroupsCombobox
            selectedItems={groupsSelected}
            onSelect={(selected) =>
              setGroupsSelected([...selected] as DefaultOptionType[])
            }
          />
        </Flex>

        <Flex flexDir="column" gap="8px">
          <Label>Product status</Label>
          <Flex gap="30px">
            {PRODUCT_STATUS_OPTIONS.map(({ label, value }) => (
              <Checkbox
                key={value}
                label={label}
                checked={productStatuses.includes(value)}
                onChange={() =>
                  setProductStatuses((prev) => toggleInArray(prev, value))
                }
              />
            ))}
          </Flex>
        </Flex>

        <Flex flexDir="column" gap="8px">
          <Label>Copy rule</Label>
          <Flex flexDir="row" gap="16px">
            {COPY_RULE_OPTIONS.map(({ label, value }) => (
              <RadioButton
                key={value}
                checked={copyRule === value}
                label={label}
                name="copyRule"
                onChange={() => setCopyRule(value)}
                value={value}
              />
            ))}
          </Flex>
        </Flex>

        <Flex justifyContent="flex-end">
          <Button
            size="medium"
            variant="primary"
            fullWidth
            disabled={!isFormValid}
            onClick={handleSubmit}
            loading={isLoading}
          >
            Load prices
          </Button>
        </Flex>
      </div>
    </LeftSidePanel>
  );
};
